import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, Users, ArrowRight } from 'lucide-react';
import { getUpcomingWorkshops } from '../../api/client';
import { useToast } from '../../contexts/ToastContext';
import LoadingScreen from '../../components/layout/LoadingScreen';

interface Workshop {
  id: number;
  title: string;
  description: string;
  duration: string;
  start_date?: string;
  start_time?: string;
  meeting_link?: string;
  price: number;
  max_seats?: number;
  seats_filled?: number;
  status: string;
}

export default function WorkshopsListScreen(): React.JSX.Element {
  const toast = useToast();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getUpcomingWorkshops();
        setWorkshops((res as any).workshops || []);
      } catch (e: any) {
        toast.show(e.message || 'Failed to load workshops', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingScreen message="Loading workshops..." />;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">Live Workshops</h1>
            <p className="text-slate-600 max-w-xl mx-auto">Join hands-on live sessions with industry experts. Limited seats available.</p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {workshops.length === 0 ? (
          <div className="text-center py-20">
            <Calendar size={48} className="mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">No upcoming workshops</h3>
            <p className="text-slate-500">Check back soon for new live sessions</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {workshops.map((w, idx) => (
              <motion.div
                key={w.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all h-full flex flex-col">
                  <div className="p-6 flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                        {w.status === 'upcoming' ? 'Upcoming' : 'Open'}
                      </span>
                      <span className="text-xs text-slate-400">{w.duration || '2 hours'}</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{w.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4">{w.description || 'Join this hands-on workshop led by industry experts.'}</p>
                    <div className="space-y-2 mb-5">
                      {w.start_date && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Calendar size={14} className="text-slate-400" />
                          <span>{new Date(w.start_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        </div>
                      )}
                      {w.start_time && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <Clock size={14} className="text-slate-400" />
                          <span>{w.start_time}</span>
                        </div>
                      )}
                      {w.meeting_link && (
                        <div className="flex items-center gap-2 text-sm text-slate-500">
                          <MapPin size={14} className="text-slate-400" />
                          <span>Online via Zoom</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-6 pb-6 pt-0">
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div>
                        <span className="text-lg font-bold text-slate-900">{w.price ? `₹${Number(w.price).toLocaleString('en-IN')}` : 'Free'}</span>
                        {w.max_seats && (
                          <span className="text-xs text-slate-400 ml-2">{w.seats_filled || 0}/{w.max_seats} seats</span>
                        )}
                      </div>
                      <Link
                        to={`/workshops/${w.id}`}
                        className="px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition-colors flex items-center gap-1"
                      >
                        Details <ArrowRight size={14} />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
